import imgWelcomeStarIcon from '../../images/welcome-star-icon.svg';
import { renderGameMessage, startBlockedMessage } from '../shared/game-message';
import { ICON_CLOSE } from '../../js/game-icons';
import imgLevelUpBadge from './level-up-badge.webp';

export const WELCOME_CONFIG = {
    icon: imgWelcomeStarIcon,
    title: 'WITAJ W GLAM PUZZLE',
    description: '<p>Twoim zadaniem jest ułożenie obrazka z elementów widocznych w panelu na ekranie.</p>' +
        '<p>Dopasowuj puzzle do pustej planszy i szukaj właściwego układu. Uważaj jednak – masz ograniczony czas, więc liczy się skupienie i sprawne działanie!</p>',
    howToPlay: {
        heading: 'JAK GRAĆ?',
        body: '<ol>' +
            '<li>Przeciągaj elementy na planszę lub kliknij element, a następnie wybrane miejsce.</li>' +
            '<li>Dopasuj puzzle do właściwych pól – poprawnie ułożony element zostanie zablokowany.</li>' +
            '<li>Ułóż cały obrazek przed końcem czasu – kolejne plansze są coraz trudniejsze.</li>' +
            '</ol>',
    },
    scoring: {
        heading: 'PUNKTACJA',
        body: '<ul>' +
            '<li>✅ Ułożenie planszy w czasie daje punkty za dany poziom.</li>' +
            '<li>🧩 Im trudniejsza plansza, tym więcej punktów.</li>' +
            '<li>⏱️ Jeśli czas planszy upłynie, przechodzisz dalej bez punktów za tę planszę.</li>' +
            '</ul>' +
            '<p>🏆 Maksymalny wynik to 50 pkt</p>',
    },
};

let state = null;
let onCompleteCb = null;
let animationFrame = null;
let pointerHandlers = [];
let heartbeatInterval = null;
let heartbeatSeq = 0;

function contentEl(modal) {
    return modal.querySelector('.game-modal__content');
}

function escHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
}

const TAB_PAD = 0.40;

function generateEdgeTypes(cols, rows) {
    const makeEdge = () => ({
        dir:    Math.random() > 0.5 ? 1 : -1,
        mid:    0.45 + Math.random() * 0.10,
        scalex: 0.80 + Math.random() * 0.20,
        scaley: 0.90 + Math.random() * 0.10,
    });
    const hEdges = Array.from({ length: rows - 1 }, () =>
        Array.from({ length: cols }, makeEdge)
    );
    const vEdges = Array.from({ length: rows }, () =>
        Array.from({ length: cols - 1 }, makeEdge)
    );
    return { hEdges, vEdges };
}

function getEdges(edgeData, row, col, cols, rows) {
    const { hEdges, vEdges } = edgeData;
    const flat = { dir: 0, mid: 0.5, scalex: 1, scaley: 1 };
    const inv  = e => ({ dir: -e.dir, mid: 1 - e.mid, scalex: e.scalex, scaley: e.scaley });
    return {
        top:    row === 0         ? flat : inv(hEdges[row - 1][col]),
        right:  col === cols - 1  ? flat : vEdges[row][col],
        bottom: row === rows - 1  ? flat : hEdges[row][col],
        left:   col === 0         ? flat : inv(vEdges[row][col - 1]),
    };
}

function drawJigsawEdge(ctx, x0, y0, x1, y1, edge) {
    const { dir, mid: m, scalex: sx, scaley: sy } = edge;
    if (dir === 0) {
        ctx.lineTo(x1, y1);
        return;
    }

    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.sqrt(dx * dx + dy * dy);
    const nx = (dy / len) * dir;
    const ny = (-dx / len) * dir;

    const pt = (t, h) => [x0 + dx * t + nx * len * h, y0 + dy * t + ny * len * h];
    const k = 1 / 12;

    const pa = pt(m - sx * k,       sy * k);
    const pb = pt(m - 2 * sx * k, 3 * sy * k);
    const pc = pt(m,              4 * sy * k);
    const pd = pt(m + 2 * sx * k, 3 * sy * k);
    const pe = pt(m + sx * k,       sy * k);

    ctx.lineTo(...pa);
    ctx.bezierCurveTo(
        ...pt(m - sx * k,     sy * k + 0.72 * k),
        ...pt(m - 2 * sx * k, 3 * sy * k - 0.92 * k),
        ...pb
    );
    ctx.bezierCurveTo(
        ...pt(m - 2 * sx * k, 3 * sy * k + 0.52 * k),
        ...pt(m - 0.8 * k,    4 * sy * k),
        ...pc
    );
    ctx.bezierCurveTo(
        ...pt(m + 0.8 * k,    4 * sy * k),
        ...pt(m + 2 * sx * k, 3 * sy * k + 0.52 * k),
        ...pd
    );
    ctx.bezierCurveTo(
        ...pt(m + 2 * sx * k, 3 * sy * k - 0.92 * k),
        ...pt(m + sx * k,     sy * k + 0.72 * k),
        ...pe
    );
    ctx.lineTo(x1, y1);
}

function buildPieceCanvas(img, cols, rows, r, c, edgeData) {
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;

    const targetAspect = cols / rows;
    const imgAspect    = iw / ih;
    let srcW, srcH, srcX, srcY;
    if (imgAspect > targetAspect) {
        srcH = ih;
        srcW = ih * targetAspect;
        srcX = (iw - srcW) / 2;
        srcY = 0;
    } else {
        srcW = iw;
        srcH = iw / targetAspect;
        srcX = 0;
        srcY = (ih - srcH) / 2;
    }

    const pieceW = srcW / cols;
    const pieceH = srcH / rows;
    const padX   = Math.ceil(pieceW * TAB_PAD);
    const padY   = Math.ceil(pieceH * TAB_PAD);

    const canvasW = Math.ceil(pieceW + 2 * padX);
    const canvasH = Math.ceil(pieceH + 2 * padY);

    const canvas = document.createElement('canvas');
    canvas.width  = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d');

    const edges = getEdges(edgeData, r, c, cols, rows);
    const ox = padX;
    const oy = padY;

    ctx.beginPath();
    ctx.moveTo(ox, oy);
    drawJigsawEdge(ctx, ox,          oy,          ox + pieceW, oy,          edges.top);
    drawJigsawEdge(ctx, ox + pieceW, oy,          ox + pieceW, oy + pieceH, edges.right);
    drawJigsawEdge(ctx, ox + pieceW, oy + pieceH, ox,          oy + pieceH, edges.bottom);
    drawJigsawEdge(ctx, ox,          oy + pieceH, ox,          oy,          edges.left);
    ctx.closePath();

    ctx.save();
    ctx.clip();

    const sx = srcX + c * pieceW - padX;
    const sy = srcY + r * pieceH - padY;
    ctx.drawImage(img, sx, sy, canvasW, canvasH, 0, 0, canvasW, canvasH);

    ctx.restore();

    ctx.strokeStyle = 'rgba(0,0,0,0.22)';
    ctx.lineWidth   = 1;
    ctx.stroke();

    return canvas.toDataURL('image/png');
}

function setHtml(modal, html) {
    const el = contentEl(modal);
    if (el) el.innerHTML = html;
}

function renderLoading(modal) {
    setHtml(modal, `<div class="pz-game"><div class="pz-pending">Ładowanie puzzla…</div></div>`);
}

function renderError(modal, msg) {
    setHtml(modal, `<div class="pz-game"><div class="pz-error">${escHtml(msg)}</div></div>`);
}

function renderBoard(modal) {
    const cols    = state.cols;
    const rows    = state.rows;
    const pieces  = state.pieces;
    const trayPieces  = pieces.filter(p => p.placedRow === null);
    const boardPieces = pieces.filter(p => p.placedRow !== null);

    const cellsHtml = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const occupant = boardPieces.find(p => p.placedRow === r && p.placedCol === c);
            const cellClass = occupant && occupant.locked ? 'pz-cell pz-cell--correct' : 'pz-cell';
            let pieceHtml = '';
            if (occupant) {
                const lockedClass = occupant.locked ? ' pz-piece--locked' : '';
                pieceHtml = `<img class="pz-piece pz-piece--placed${lockedClass} js-pz-piece"
                    data-token="${escHtml(occupant.token)}"
                    src="${occupant.src}" alt="" draggable="false">`;
            }
            cellsHtml.push(`<div class="${cellClass}" data-row="${r}" data-col="${c}">${pieceHtml}</div>`);
        }
    }

    const trayHtml = trayPieces.map(p => `
        <div class="pz-tray__slot">
            <img class="pz-piece pz-piece--tray js-pz-piece"
                data-token="${escHtml(p.token)}"
                src="${p.src}" alt="" draggable="false">
        </div>
    `).join('');

    setHtml(modal, `
        <div class="pz-game">
            <div class="pz-hud">
                <div class="pz-hud__level">PRÓBA ${state.levelIndex + 1}/${state.levelsTotal}</div>
                <div class="pz-hud__timer js-pz-timer">--:--</div>
                <button type="button" class="pz-hud__close js-game-modal-close" aria-label="Zamknij grę">${ICON_CLOSE}</button>
            </div>
            <div class="pz-board js-pz-board" style="--pz-cols: ${cols}; --pz-rows: ${rows};">
                ${cellsHtml.join('')}
            </div>
            <div class="pz-tray js-pz-tray">${trayHtml}</div>
        </div>
    `);

    attachPointerHandlers(modal);
    tickTimer(modal);
}

const GESTURE_PX = 8;
let activePointer = null;

function attachPointerHandlers(modal) {
    pointerHandlers.forEach(off => off());
    pointerHandlers = [];

    const root = contentEl(modal);

    const onDown = (e) => {
        const pieceEl = e.target.closest('.js-pz-piece');
        const cellEl  = e.target.closest('.pz-cell');

        // Start a drag/tap on a (non-locked) piece.
        if (pieceEl && !pieceEl.classList.contains('pz-piece--locked')) {
            e.preventDefault();
            const pt = pointFromEvent(e);
            activePointer = {
                token: pieceEl.getAttribute('data-token'),
                startX: pt.x,
                startY: pt.y,
                startEl: pieceEl,
                cellEl,
                moved: false,
                floatEl: null,
            };
            return;
        }

        // Tap on a board cell while a piece is selected → tap-to-place gesture.
        if (cellEl && selectedToken) {
            e.preventDefault();
            const pt = pointFromEvent(e);
            activePointer = {
                token: null,
                startX: pt.x,
                startY: pt.y,
                startEl: null,
                cellEl,
                moved: false,
                floatEl: null,
            };
        }
    };

    const onMove = (e) => {
        if (!activePointer) return;
        if (!activePointer.startEl) return;
        e.preventDefault();

        const pt = pointFromEvent(e);
        const dx = pt.x - activePointer.startX;
        const dy = pt.y - activePointer.startY;
        if (!activePointer.moved && Math.hypot(dx, dy) < GESTURE_PX) return;

        activePointer.moved = true;

        if (!activePointer.floatEl) {
            const rect = activePointer.startEl.getBoundingClientRect();
            const clone = activePointer.startEl.cloneNode(true);
            clone.classList.add('pz-piece--float');
            clone.style.position = 'fixed';
            clone.style.left     = rect.left + 'px';
            clone.style.top      = rect.top + 'px';
            clone.style.width    = rect.width + 'px';
            clone.style.height   = rect.height + 'px';
            clone.style.pointerEvents = 'none';
            clone.style.zIndex   = '9999';
            clone.style.transition = 'none';
            clone.style.margin = '0';
            document.body.appendChild(clone);
            activePointer.floatEl = clone;
            activePointer.startEl.style.visibility = 'hidden';
        }

        activePointer.floatEl.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
    };

    const onUp = (e) => {
        if (!activePointer) return;
        const pt = pointFromEvent(e);

        if (activePointer.floatEl) activePointer.floatEl.remove();
        if (activePointer.startEl) activePointer.startEl.style.visibility = '';

        if (!activePointer.moved) {
            if (activePointer.token) {
                // Tapped a piece: place a previously selected piece onto this
                // (placed) piece's cell, otherwise (re)select the tapped piece.
                if (selectedToken && selectedToken !== activePointer.token && activePointer.cellEl) {
                    placePieceOnCell(modal, selectedToken, activePointer.cellEl);
                } else {
                    handleTap(modal, activePointer.token);
                }
            } else if (activePointer.cellEl && selectedToken) {
                // Tapped a board cell with a piece selected → place it there.
                placePieceOnCell(modal, selectedToken, activePointer.cellEl);
            }
        } else if (activePointer.token) {
            const targetCell = cellAtPoint(root, pt.x, pt.y);
            if (targetCell) {
                placePieceOnCell(modal, activePointer.token, targetCell);
            }
        }

        activePointer = null;
    };

    root.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    pointerHandlers.push(
        () => root.removeEventListener('pointerdown', onDown),
        () => window.removeEventListener('pointermove', onMove),
        () => window.removeEventListener('pointerup', onUp),
        () => window.removeEventListener('pointercancel', onUp),
    );
}

function pointFromEvent(e) {
    return { x: e.clientX, y: e.clientY };
}

function cellAtPoint(root, x, y) {
    const el = document.elementFromPoint(x, y);
    if (!el) return null;
    return el.closest('.pz-cell');
}

let selectedToken = null;

function handleTap(modal, token) {
    if (selectedToken === token) {
        selectedToken = null;
        syncSelectionClass(modal);
        return;
    }
    selectedToken = token;
    syncSelectionClass(modal);
}

function syncSelectionClass(modal) {
    const root = contentEl(modal);
    root.querySelectorAll('.pz-piece').forEach((el) => {
        el.classList.toggle('pz-piece--selected',
            el.getAttribute('data-token') === selectedToken);
    });
}

function placePieceOnCell(modal, token, cellEl) {
    const r = parseInt(cellEl.getAttribute('data-row'), 10);
    const c = parseInt(cellEl.getAttribute('data-col'), 10);

    const piece = state.pieces.find(p => p.token === token);
    if (!piece || piece.locked) return;

    const occupant = state.pieces.find(p => p.placedRow === r && p.placedCol === c);
    if (occupant && occupant.locked) return;

    if (occupant && occupant.token !== token) {
        occupant.placedRow = piece.placedRow;
        occupant.placedCol = piece.placedCol;
        if (occupant.placedRow === occupant.correctRow
            && occupant.placedCol === occupant.correctCol) {
            occupant.locked = true;
        }
    } else if (occupant && occupant.token === token) {
        return;
    }

    piece.placedRow = r;
    piece.placedCol = c;
    state.moveCount++;

    if (r === piece.correctRow && c === piece.correctCol) {
        piece.locked = true;
    }

    selectedToken = null;
    renderBoard(modal);

    if (state.pieces.every(p => p.locked)) {
        submitPuzzle(modal);
    }
}

function stopHeartbeat() {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
}

function startHeartbeat() {
    stopHeartbeat();
    heartbeatInterval = setInterval(() => {
        if (!state || state.submitted) { stopHeartbeat(); return; }
        heartbeatSeq++;
        const elapsedMs    = Date.now() - state.clientStartedAt;
        const lockedCount  = state.pieces.filter(p => p.locked).length;
        fetch(`/api/task/${state.taskId}/puzzle/heartbeat`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({
                sessionToken: state.sessionToken,
                seq:          heartbeatSeq,
                elapsedMs,
                lockedCount,
            }),
        }).catch(() => { /* fire-and-forget */ });
    }, 10000);
}

function tickTimer(modal) {
    cancelAnimationFrame(animationFrame);
    const tick = () => {
        if (!state || state.submitted) return;
        const now     = Date.now();
        const elapsed = Math.floor((now - state.clientStartedAt) / 1000);
        const left    = Math.max(0, state.timeLimitSeconds - elapsed);
        const m       = Math.floor(left / 60).toString().padStart(2, '0');
        const s       = (left % 60).toString().padStart(2, '0');
        const timerEl = contentEl(modal).querySelector('.js-pz-timer');
        if (timerEl) timerEl.textContent = `${m}:${s}`;

        if (left <= 0) {
            submitPuzzle(modal);
            return;
        }
        animationFrame = requestAnimationFrame(tick);
    };
    tick();
}

function applyBoard(modal, payload) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => renderError(modal, 'Nie udało się załadować obrazka.');
    img.onload = () => {
        const cols     = payload.cols;
        const rows     = payload.rows;
        const edgeData = generateEdgeTypes(cols, rows);

        let pieces;
        try {
            pieces = payload.pieces.map((p) => {
                const sourceIndex = p.sourceIndex;
                const correctRow  = Math.floor(sourceIndex / cols);
                const correctCol  = sourceIndex % cols;
                return {
                    token: p.token,
                    correctRow,
                    correctCol,
                    src: buildPieceCanvas(img, cols, rows, correctRow, correctCol, edgeData),
                    placedRow: null,
                    placedCol: null,
                    locked: false,
                };
            });
        } catch (err) {
            renderError(modal, 'Nie udało się przygotować puzzli (problem z obrazkiem).');
            return;
        }

        heartbeatSeq = 0;
        state = {
            taskId:           payload.taskId,
            sessionToken:     payload.sessionToken,
            cols, rows,
            pieces,
            timeLimitSeconds: payload.timeLimitSeconds,
            level:            payload.level,
            levelIndex:       payload.levelIndex,
            levelsTotal:      payload.levelsTotal,
            clientStartedAt:  Date.now(),
            submitted:        false,
            moveCount:        0,
        };

        startHeartbeat();
        renderBoard(modal);
    };
    img.src = payload.imageUrl;
}

async function startGame(modal, data) {
    renderLoading(modal);

    let startResp;
    try {
        const res = await fetch(`/api/task/${data.task.id}/puzzle/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
        });
        startResp = await res.json();
        if (!res.ok) {
            if (res.status === 409 && (startResp?.error === 'already_completed' || startResp?.error === 'game_in_progress')) {
                renderGameMessage(modal, startBlockedMessage(startResp?.error));
                return;
            }
            renderError(modal, 'Błąd rozpoczęcia gry.');
            return;
        }
    } catch {
        renderError(modal, 'Błąd połączenia.');
        return;
    }

    applyBoard(modal, {
        taskId:           data.task.id,
        sessionToken:     startResp.sessionToken,
        cols:             startResp.cols,
        rows:             startResp.rows,
        pieces:           startResp.pieces,
        imageUrl:         startResp.imageUrl,
        timeLimitSeconds: startResp.timeLimitSeconds,
        level:            startResp.level,
        levelIndex:       startResp.levelIndex,
        levelsTotal:      startResp.levelsTotal,
    });
}

function renderTransition(modal, resp) {
    setHtml(modal, `
        <div class="pz-game pz-game--transition">
            <div class="pz-transition">
                <button type="button" class="pz-transition__close js-game-modal-close" aria-label="Zamknij grę">${ICON_CLOSE}</button>
                <h2 class="pz-transition__title">IDZIE CI ŚWIETNIE!</h2>
                <p class="pz-transition__subtitle">Przygotuj się na więcej puzzli do ułożenia ;)</p>
                <div class="pz-transition__badge"><img src="${imgLevelUpBadge}" alt="" draggable="false"></div>
                <button type="button" class="pz-transition__next js-pz-next">NASTĘPNY POZIOM</button>
            </div>
        </div>
    `);

    const btn = contentEl(modal).querySelector('.js-pz-next');
    if (btn) {
        // No { once: true }: a self-removing listener silently dies on a double-tap
        // or a slow next-level image, leaving a dead button. Instead guard against
        // re-entry and immediately swap to the loading screen — that removes the
        // button from the DOM (so a second tap can't land on it) and gives the user
        // feedback while the next board's image loads.
        let advancing = false;
        btn.addEventListener('click', () => {
            if (advancing) return;
            advancing = true;
            renderLoading(modal);
            applyBoard(modal, {
                taskId:           state.taskId,
                sessionToken:     state.sessionToken,
                cols:             resp.cols,
                rows:             resp.rows,
                pieces:           resp.pieces,
                imageUrl:         resp.imageUrl,
                timeLimitSeconds: resp.timeLimitSeconds,
                level:            resp.level,
                levelIndex:       resp.levelIndex,
                levelsTotal:      resp.levelsTotal,
            });
        });
    }
}

async function submitPuzzle(modal) {
    if (!state || state.submitted) return;
    state.submitted = true;
    cancelAnimationFrame(animationFrame);
    stopHeartbeat();

    const submittedLevelIndex = state.levelIndex;
    const placements = state.pieces
        .filter(p => p.placedRow !== null && p.placedCol !== null)
        .map(p => ({ token: p.token, row: p.placedRow, col: p.placedCol }));

    let resp;
    try {
        const res = await fetch(`/api/task/${state.taskId}/puzzle/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                sessionToken: state.sessionToken,
                placements,
                moves: state.moveCount,
                levelIndex: submittedLevelIndex,
            }),
        });
        resp = await res.json();
        if (!res.ok) {
            renderError(modal, resp?.error === 'already_completed'
                ? 'Zadanie zostało już zaliczone wcześniej.'
                : 'Błąd zapisu wyniku.');
            return;
        }
    } catch {
        renderError(modal, 'Błąd połączenia przy wysyłaniu wyniku.');
        return;
    }

    if (resp.transition) {
        // Plansza wysłana, czekamy na „NASTĘPNY POZIOM". Brak aktywnej planszy/timera/heartbeatu,
        // więc odblokowujemy submitted, by zamknięcie gry wywołało abandon (zachowa naliczone punkty).
        state.submitted = false;
        renderTransition(modal, resp);
        return;
    }

    if (onCompleteCb) onCompleteCb(resp);
    document.dispatchEvent(new CustomEvent('game-modal:result-show', {
        detail: {
            rankingPoints: resp.rankingPointsEarned ?? 0,
            redeemablePoints: resp.redeemablePointsEarned ?? 0,
        },
    }));
}

export function renderPuzzle(modal, data, onComplete) {
    onCompleteCb = onComplete || null;
    modal.classList.add('game-modal--puzzle');
    startGame(modal, data);
}

export async function forceEndPuzzle() {
    if (!state || state.submitted) return;

    const taskId       = state.taskId;
    const sessionToken = state.sessionToken;
    const cb           = onCompleteCb;

    const placements = state.pieces
        .filter(p => p.placedRow !== null && p.placedCol !== null)
        .map(p => ({ token: p.token, row: p.placedRow, col: p.placedCol }));

    state.submitted = true;
    cancelAnimationFrame(animationFrame);
    stopHeartbeat();
    pointerHandlers.forEach(off => off());
    pointerHandlers = [];
    state         = null;
    onCompleteCb  = null;
    selectedToken = null;
    activePointer = null;

    let resp = null;
    try {
        const res = await fetch(`/api/task/${taskId}/puzzle/abandon`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ sessionToken, placements }),
        });
        resp = await res.json();
    } catch {
        resp = { completed: false, rankingPointsEarned: 0, redeemablePointsEarned: 0 };
    }

    if (cb) cb(resp);
    document.dispatchEvent(new CustomEvent('game-modal:result-show', {
        detail: {
            rankingPoints:    resp.rankingPointsEarned    ?? 0,
            redeemablePoints: resp.redeemablePointsEarned ?? 0,
        },
    }));
}

export function teardownPuzzle(modal) {
    cancelAnimationFrame(animationFrame);
    stopHeartbeat();
    pointerHandlers.forEach(off => off());
    pointerHandlers = [];
    state         = null;
    selectedToken = null;
    activePointer = null;
    onCompleteCb  = null;
    if (modal) modal.classList.remove('game-modal--puzzle');
}
